import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/cn";

import { Appear } from "@/components/landing/Appear";
import { RevealText } from "@/components/landing/RevealText";

const FAQ: { question: string; answer: string[] }[] = [
  {
    question: "Как Spider подключается к бирже?",
    answer: [
      "Через API-ключ, который вы создаёте сами в личном кабинете биржи. При создании ключу нужно выдать только два права: читать данные и совершать сделки.",
      "Право на вывод средств выдавать не нужно. Более того, ключ с таким правом наша система не примет и попросит создать новый. Деньги всё время остаются на вашем счёте на бирже — Spider лишь отправляет туда торговые поручения.",
    ],
  },
  {
    question: "Может ли бот вывести мои деньги?",
    answer: [
      "Нет. Это не вопрос настроек или доверия: у ключа, с которым работает Spider, просто нет права на вывод, и биржа отклонит такую операцию, даже если её запросить.",
      "Всё, что бот может делать, — открывать и закрывать сделки внутри вашего счёта.",
    ],
  },
  {
    question: "Как считается комиссия?",
    answer: [
      "Прямой комиссии, которую мы списывали бы с вашего счёта, нет. Вместо неё бот расходует топливо — внутренний баланс, который вы пополняете заранее.",
      "Списание происходит только с прибыльных сделок и составляет 30% от заработанного. Если сделка закрылась без прибыли, топливо за неё не тратится. Подписки и платы за время работы тоже нет.",
    ],
  },
  {
    question: "Что такое топливо?",
    answer: [
      "Топливо — это внутренняя валюта Spider, которой оплачивается его работа. Вы покупаете его заранее, на любую сумму, и оно лежит на вашем балансе внутри сервиса.",
      "Каждый раз, когда бот закрывает сделку с прибылью, с этого баланса списывается 30% от заработанного. Пока топливо есть — бот торгует. Когда оно заканчивается — останавливается, и вы сами решаете, пополнять запас или нет.",
      "Именно поэтому мы никогда не обращаемся к вашим деньгам на бирже: комиссия берётся не со счёта, а из запаса, который вы пополнили заранее и всегда контролируете.",
    ],
  },
  {
    question: "Можно ли остановить бота в любой момент?",
    answer: [
      "Да, остановка доступна всегда и не требует подтверждений — новые сделки бот сразу перестаёт открывать.",
      "Уже открытые сделки закрываются не мгновенно: боту нужно время, чтобы довести их до выгодной точки, а не фиксировать убыток из-за резкого выхода.",
    ],
  },
  {
    question: "Нужен ли опыт в трейдинге?",
    answer: [
      "Нет. От вас требуется подключить биржу, выбрать комфортный уровень риска и пополнить топливо. Дальше бот работает сам: анализирует рынок, открывает и закрывает сделки.",
      "Разбираться в графиках, индикаторах и стратегиях не нужно.",
    ],
  },
  {
    question: "Есть ли демо-режим?",
    answer: [
      "Да. Биржа позволяет создать отдельный API-ключ для демо-окружения — он работает с виртуальными деньгами.",
      "Создайте такой ключ и добавьте его в аккаунт так же, как обычный. Бот будет торговать по той же логике, но без реальных средств, так что можно спокойно посмотреть, как он себя ведёт.",
    ],
  },
  {
    question: "Что будет, если бот не заработает прибыль?",
    answer: [
      "Топливо расходуется только с прибыльных сделок, поэтому в такие периоды списаний не происходит и купленный запас остаётся на балансе.",
      "При этом торговля на бирже связана с риском, и убыточные сделки возможны. Границы этого риска вы задаёте сами при настройке, и бот за них не выходит.",
    ],
  },
];

interface FaqItemProps {
  question: string;
  answer: string[];
  open: boolean;
  onToggle: () => void;
}

const FaqItem = ({ question, answer, open, onToggle }: FaqItemProps) => (
  <li className="border-landing-line border-b">
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="group flex w-full cursor-pointer items-center justify-between gap-6 py-6 text-left"
    >
      <span className="text-[1rem] leading-none font-semibold text-[#111111]">{question}</span>
      <ChevronDown
        aria-hidden
        className={cn(
          "size-5 shrink-0 text-[#666666] transition-transform duration-300",
          open && "rotate-180"
        )}
      />
    </button>

    <div
      className={cn(
        "grid transition-[grid-template-rows] duration-300 ease-out",
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      )}
    >
      <div className="overflow-hidden">
        <div className="text-landing-ink/60 flex max-w-[50rem] flex-col gap-3 pb-6 text-[1rem] leading-[1.5] font-medium">
          {answer.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </div>
    </div>
  </li>
);

export const FaqSection = () => {
  const [openItems, setOpenItems] = useState<Set<number>>(() => new Set());

  const toggle = (index: number) =>
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    <section
      id="faq"
      className="border-landing-line scroll-mt-[5.625rem] border-b"
    >
      <div className="landing-container pt-[6.25rem] pb-[7.5rem] max-md:py-20">
        <RevealText
          as="h2"
          className="text-landing-ink text-[2.25rem] leading-none font-semibold max-md:text-[1.5rem]"
        >
          Частые вопросы
        </RevealText>

        <Appear
          as="ul"
          className="border-landing-line mt-12 border-t max-md:mt-8"
        >
          {FAQ.map((item, index) => (
            <FaqItem
              key={item.question}
              question={item.question}
              answer={item.answer}
              open={openItems.has(index)}
              onToggle={() => toggle(index)}
            />
          ))}
        </Appear>
      </div>
    </section>
  );
};
